import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, Link } from '@/lib/router-compat';
import { SEOHreflang } from '@/components/seo/SEOHreflang';
import { useCanonicalUrl } from '@/hooks/useCanonicalUrl';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ModernListingCard } from '@/components/listings/card/ModernListingCard';
import { CompactMobileListingCard } from '@/components/listings/card/CompactMobileListingCard';
import { QuickViewModal } from '@/components/listings/card/QuickViewModal';
import { VehicleType } from '@/types/car';
import { MagnifyingGlass as Search, Plus, Eye, CircleNotch as Loader2 } from '@phosphor-icons/react';
import { ListingsPagination } from '@/components/listings/card/ListingsPagination';
import { classifyListSearch } from '@/utils/listSearchParams';
import { CarGridSkeleton } from '@/components/performance/ImprovedSkeletons';
import { useIsMobile } from '@/hooks/use-mobile';
import { useScrollToResults } from '@/hooks/useScrollToResults';

import { CompareInfoBanner } from '@/components/listings/card/CompareInfoBanner';
import { INTENT_PRESETS } from '@/components/home/IntentChips';
import { Sparkles, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';


import { ViewToggle, useViewMode } from '@/components/listings/card/ViewToggle';
import { CarCardList } from '@/components/listings/card/CarCardList';
import { usePaginatedListings } from '@/hooks/usePaginatedListings';
import { ListingsGridV2 } from '@/components/listings/card/ListingsGridV2';
import { useCategoryCounts } from '@/hooks/useCategoryCounts';
import { ListCityPicker } from '@/components/listings/card/ListCityPicker';
import type { CategoryCode } from '@/types/listings-v2';
import { AutosuggestSearch } from '@/components/listings/filters/AutosuggestSearch';
// MobileQuickFilters removed — replaced by VehiclesFilterBar to avoid duplicate filter UI
import { useTranslationV2 } from '@/hooks/useTranslationV2';
import { useCountry } from '@/hooks/useCountry';
import { EmptyStateListings } from '@/components/listings/card/EmptyStateListings';
import { useQueryClient } from '@tanstack/react-query';
import { Checkbox } from '@/components/ui/checkbox';
import { Globe } from '@phosphor-icons/react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { SaveSearchDialog } from '@/components/listings/filters/SaveSearchDialog';
import { readSavedSearchFields } from '@/utils/savedSearchFilters';
import { FAQSchema } from '@/components/seo/schema/FAQSchema';
import { VehiclesFilterBar } from '@/components/vehicles/VehiclesFilterBar';
import { ActiveFilterChips } from '@/components/vehicles/ActiveFilterChips';
import { CategoryShowcaseGrid } from '@/components/vehicles/CategoryShowcaseGrid';
import { NLSearchBar } from '@/components/vehicles/NLSearchBar';
import valuationCopy from '@/data/translations/ro/valuation.json';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { VEHICLE_SORTS, resolveVehicleSort, vehicleSortSearch } from '@/utils/vehicleSort';
import { trackVehiclesSort } from '@/utils/buyerJourneyTracking';
import roBuyer from '@/data/translations/ro/buyer.json';
import enBuyer from '@/data/translations/en/buyer.json';
import deBuyer from '@/data/translations/de/buyer.json';

const ITEMS_PER_PAGE = 10;

/** Fill missing filters from the shared /cars aliases (pret_max, an_min, km_max, combustibil…). */
function applySavedSearchAliases(target: MobileFilters, src: URLSearchParams) {
  const f = readSavedSearchFields(src);
  const n = (v?: string) => { if (!v) return undefined; const x = Number(v); return Number.isFinite(x) ? x : undefined; };
  target.make ??= f.marca;
  target.model ??= f.model;
  target.priceFrom ??= n(f.pretMin);
  target.priceTo ??= n(f.pretMax);
  target.yearFrom ??= n(f.anMin);
  target.yearTo ??= n(f.anMax);
  target.mileageTo ??= n(f.kmMax);
  target.fuelType ??= f.combustibil;
  target.transmission ??= f.transmisie;
  target.location ??= f.locatie;
}

/** Read filters from the URL query string so links like /vehicles?make=BMW actually filter. */
function parseFiltersFromSearch(search: string): MobileFilters {
  const p = new URLSearchParams(search);
  const num = (k: string) => {
    const v = p.get(k);
    if (v == null || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const str = (k: string) => p.get(k) || undefined;

  const next: MobileFilters = {
    make: str('make') || str('marca') || str('brand'),
    model: str('model'),
    search: str('search') || str('q'),
    fuelType: str('fuelType') || str('fuel'),
    transmission: str('transmission'),
    condition: str('condition'),
    location: str('location') || str('city'),
    bodyType: str('bodyType') || str('body'),
    priceFrom: num('priceFrom') ?? num('priceMin'),
    priceTo: num('priceTo') ?? num('priceMax'),
    yearFrom: num('yearFrom') ?? num('yearMin'),
    yearTo: num('yearTo') ?? num('yearMax'),
    mileageFrom: num('mileageFrom'),
    mileageTo: num('mileageTo') ?? num('mileageMax'),
    seats: num('seats'),
    powerFrom: num('powerFrom'),
    powerTo: num('powerTo'),
    doors: num('doors'),
    driveType: str('driveType'),
    color: str('color'),
    conditionTechnical: str('conditionTech'),
    sellerType: str('sellerType'),
    features: str('features') ? str('features')!.split(',').filter(Boolean) : undefined,
  };

  applySavedSearchAliases(next, p);
  Object.keys(next).forEach((k) => {
    if ((next as any)[k] === undefined) delete (next as any)[k];
  });
  return next;
}


interface MobileFilters {
  // Legacy names (from RN sheet)
  priceMin?: number;
  priceMax?: number;
  yearMin?: number;
  yearMax?: number;
  fuelType?: string;
  transmission?: string;
  condition?: string;
  // Web sheet names
  priceFrom?: number;
  priceTo?: number;
  yearFrom?: number;
  yearTo?: number;
  mileageFrom?: number;
  mileageTo?: number;
  location?: string;
  vehicleType?: VehicleType;
  // Search fields
  make?: string;
  model?: string;
  search?: string;
  // Country specific
  rhd?: boolean;
  mot_valid?: boolean;
  ulez_compliant?: boolean;
  euro_norm?: string;
  environmental_badge?: string;
  // Seats filter
  seats?: number;
  // Body type (filtered client-side, RPC doesn't support it)
  bodyType?: string;
  // Advanced filters (power, doors, drive, color, condition, seller, features)
  powerFrom?: number;
  powerTo?: number;
  doors?: number;
  driveType?: string;
  color?: string;
  conditionTechnical?: string;
  sellerType?: string;
  features?: string[];
}

const Vehicles: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const selectedVehicleType = searchParams.get('type') as VehicleType || undefined;
  const dealerId = searchParams.get('dealer') || null;
  const canonicalUrl = useCanonicalUrl();
  const { t, language } = useTranslationV2('vehicles');
  const { t: tFilters } = useTranslationV2('filters');
  const { country } = useCountry();
  const buyer = country === 'DE' ? deBuyer : country === 'UK' ? enBuyer : roBuyer;
  const sort = resolveVehicleSort(searchParams.get('sort'));
  const sortQuery = VEHICLE_SORTS[sort];
  const handleSortChange = (value: string) => {
    const next = resolveVehicleSort(value);
    if (next === sort) return;
    trackVehiclesSort(next);
    setCurrentPage(1);
    navigate(`?${vehicleSortSearch(location.search, next).toString()}#results`);
  };
  const queryClient = useQueryClient();
  
  // Map vehicle types to category codes
  const categoryCodeMap: Record<VehicleType, CategoryCode> = {
    car: 'cars',
    truck: 'heavy_truck',
    motorcycle: 'motorcycle',
    van: 'van',
    trailer: 'trailer',
    agricultural: 'agricultural',
    construction: 'construction',
    boat: 'boat',
    atv: 'atv'
  };
  
  const categoryCode = selectedVehicleType ? categoryCodeMap[selectedVehicleType] : undefined;
  
  const [selectedVehicle, setSelectedVehicle] = useState<any | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [filters, setFilters] = useState<MobileFilters>(() => parseFiltersFromSearch(location.search));
  const [searchAllCountries, setSearchAllCountries] = useState(false);
  const pageFromSearch = (search: string) => {
    const n = parseInt(new URLSearchParams(search).get('page') || '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };
  // Initialise from the URL so ?page=N renders that page server-side too.
  const [currentPage, setCurrentPage] = useState(() => pageFromSearch(location.search));
  const vehiclesListKind = classifyListSearch(new URLSearchParams(location.search));
  const [aiSearchOpen, setAiSearchOpen] = useState(false);
  const isMobile = useIsMobile();
  const resultsRef = React.useRef<HTMLDivElement | null>(null);
  const { viewMode, setViewMode } = useViewMode();

  // Keep filters in sync when the query string changes (e.g. brand tiles on home)
  const searchKey = location.search;
  useEffect(() => {
    setFilters(parseFiltersFromSearch(searchKey));
    setCurrentPage(pageFromSearch(searchKey));
  }, [searchKey]);


  const handleApplyFilters = (next: MobileFilters) => {
    const newSearchAll = (next as any).search_all_countries === 'true';
    setFilters(next);
    setSearchAllCountries(newSearchAll);
    setCurrentPage(1); // Reset to first page when filters change

    // Update URL without navigation/reload
    const params = new URLSearchParams();
    const type = next.vehicleType || selectedVehicleType;
    if (type) params.set('type', type);
    if (next.priceFrom != null) params.set('priceFrom', String(next.priceFrom));
    if (next.priceTo != null) params.set('priceTo', String(next.priceTo));
    if (next.yearFrom != null) params.set('yearFrom', String(next.yearFrom));
    if (next.yearTo != null) params.set('yearTo', String(next.yearTo));
    if (next.mileageFrom != null) params.set('mileageFrom', String(next.mileageFrom));
    if (next.mileageTo != null) params.set('mileageTo', String(next.mileageTo));
    if (next.fuelType) params.set('fuelType', next.fuelType);
    if (next.transmission) params.set('transmission', next.transmission);
    if (next.location) params.set('location', next.location);
    if (next.make) params.set('make', next.make);
    if (next.model) params.set('model', next.model);
    if (next.powerFrom != null) params.set('powerFrom', String(next.powerFrom));
    if (next.powerTo != null) params.set('powerTo', String(next.powerTo));
    if (next.doors != null) params.set('doors', String(next.doors));
    if (next.driveType) params.set('driveType', next.driveType);
    if (next.color) params.set('color', next.color);
    if (next.conditionTechnical) params.set('conditionTech', next.conditionTechnical);
    if (next.sellerType) params.set('sellerType', next.sellerType);
    if (next.features && next.features.length) params.set('features', next.features.join(','));
    if (newSearchAll) params.set('search_all_countries', 'true');
    if (searchParams.has('sort')) params.set('sort', sort);

    const qs = params.toString();
    const newUrl = qs ? `/vehicles?${qs}` : '/vehicles';
    window.history.pushState({}, '', newUrl);

    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Use server-side filtered pagination for fast loading
  const priceMin = filters.priceMin ?? filters.priceFrom;
  const priceMax = filters.priceMax ?? filters.priceTo;
  const yearMin = filters.yearMin ?? filters.yearFrom;
  const yearMax = filters.yearMax ?? filters.yearTo;

  // Expand bodyType into synonyms list for server-side filtering
  const bodyTypesParam = useMemo(() => {
    if (!filters.bodyType) return null;
    const synonyms: Record<string, string[]> = {
      estate: ['estate', 'break', 'combi', 'kombi', 'station wagon', 'familial'],
      suv: ['suv', 'crossover', 'jeep', '4x4'],
      // preset "Mașina de familie": SUV + break/combi + monovolum
      family: [
        'suv', 'crossover', 'jeep', '4x4',
        'estate', 'break', 'combi', 'kombi', 'station wagon', 'familial',
        'minivan', 'monovolum', 'mpv',
      ],
      sedan: ['sedan', 'berlina', 'berlină', 'limousine', 'limuzina'],
      hatchback: ['hatchback', 'sportback', 'compact'],
      coupe: ['coupe', 'coupé'],
      cabrio: ['cabrio', 'cabriolet', 'convertible', 'roadster'],
      minivan: ['minivan', 'monovolum', 'mpv'],
      pickup: ['pickup', 'pick-up', 'pick up'],
      van: ['van', 'duba', 'dubă', 'doka', 'autoutilitara', 'autoutilitară'],
    };
    const key = filters.bodyType.toLowerCase();
    return synonyms[key] || [key];
  }, [filters.bodyType]);

  const {
    data: paginatedData,
    isLoading: listingsLoading,
  } = usePaginatedListings({
    country_code: searchAllCountries ? null : country,
    category_code: categoryCode,
    make: filters.make || null,
    model: filters.model || null,
    price_min: priceMin || null,
    price_max: priceMax || null,
    year_min: yearMin || null,
    year_max: yearMax || null,
    mileage_min: filters.mileageFrom || null,
    mileage_max: filters.mileageTo || null,
    fuel: filters.fuelType || null,
    transmission: filters.transmission || null,
    condition: filters.condition || null,
    sort_by: sortQuery.sort_by,
    sort_order: sortQuery.sort_order,
    pageSize: ITEMS_PER_PAGE,
    page: currentPage,
    user_id: dealerId,
    location: filters.location || null,
    seats: filters.seats || null,
    body_types: bodyTypesParam,
    euro_norm: filters.euro_norm || null,
    environmental_badge: filters.environmental_badge || null,
    power_min: filters.powerFrom || null,
    power_max: filters.powerTo || null,
    doors: filters.doors || null,
    drive_type: filters.driveType || null,
    color: filters.color || null,
    condition_technical: filters.conditionTechnical || null,
    seller_type: filters.sellerType || null,
    features: filters.features || null,
  });

  const listings = paginatedData?.pages[0]?.listings || [];
  const totalCount = paginatedData?.pages[0]?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const loading = listingsLoading;

  console.log('🚗 [Vehicles] usePaginatedListings result:', { 
    count: listings.length, 
    totalCount,
    loading: listingsLoading,
    country: searchAllCountries ? 'ALL' : country,
    category: categoryCode 
  });

  // Location filtering is now done server-side via p_location parameter
  // No need for client-side filtering anymore

  // Initialize filters from URL on first load
  React.useEffect(() => {
    const init: MobileFilters = {};
    const num = (k: string) => {
      const v = searchParams.get(k);
      return v ? Number(v) : undefined;
    };
    init.vehicleType = (searchParams.get('type') as VehicleType) || undefined;

    // Support both legacy and web param names (camelCase and snake_case)
    init.priceFrom = num('priceFrom') ?? num('priceMin') ?? num('price_min');
    init.priceTo = num('priceTo') ?? num('priceMax') ?? num('price_max');
    init.yearFrom = num('yearFrom') ?? num('yearMin') ?? num('year_min');
    init.yearTo = num('yearTo') ?? num('yearMax') ?? num('year_max');
    init.mileageFrom = num('mileageFrom') ?? num('mileage_min');
    init.mileageTo = num('mileageTo') ?? num('mileage_max');

    init.fuelType = searchParams.get('fuelType') || searchParams.get('fuel') || undefined;
    init.transmission = searchParams.get('transmission') || undefined;

    // City/region/location harmonization
    init.location = searchParams.get('location') || searchParams.get('city') || searchParams.get('region') || undefined;

    // Search fields
    init.make = searchParams.get('make') || undefined;
    init.model = searchParams.get('model') || undefined;
    init.search = searchParams.get('q') || undefined;
    
    // Seats filter (for family cars, 7-seaters, etc.)
    init.seats = num('seats');
    applySavedSearchAliases(init, searchParams);

    // Body type (SUV, Estate, Sedan, Hatchback, etc.) — filtered client-side
    init.bodyType = searchParams.get('bodyType') || searchParams.get('body_type') || undefined;

    // Country specific (Euro emission norm / DE environmental badge)
    init.euro_norm = searchParams.get('euro_norm') || undefined;
    init.environmental_badge = searchParams.get('environmental_badge') || undefined;

    // Advanced filters
    init.powerFrom = num('powerFrom');
    init.powerTo = num('powerTo');
    init.doors = num('doors');
    init.driveType = searchParams.get('driveType') || undefined;
    init.color = searchParams.get('color') || undefined;
    init.conditionTechnical = searchParams.get('conditionTech') || undefined;
    init.sellerType = searchParams.get('sellerType') || undefined;
    const featuresParam = searchParams.get('features');
    if (featuresParam) init.features = featuresParam.split(',').filter(Boolean);

    // Check if search_all_countries is set in URL
    const searchAllParam = searchParams.get('search_all_countries') === 'true';
    setSearchAllCountries(searchAllParam);
    
    // Get page from URL
    const pageParam = searchParams.get('page');
    if (pageParam) {
      setCurrentPage(parseInt(pageParam, 10));
    }

    if (Object.values(init).some((v) => v !== undefined)) {
      setFilters(init);
    }
  }, [location.search]);

  // Scroll straight to the results when arriving with an active search
  // (homepage search form, brand tiles, city links, #results hash).
  useScrollToResults({
    key: `${location.pathname}${location.search}`,
    active:
      Array.from(new URLSearchParams(location.search).keys()).length > 0 ||
      (typeof window !== 'undefined' && window.location.hash === '#results'),
    ready: !loading,
  });


  // Displayed listings - location filtering now done server-side
  const displayedListings = listings;

  // Handle page change
  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    navigate(`?${params.toString()}#results`);
  };

  const handleVehicleTypeChange = (type: VehicleType) => {
    setFilters({});
    
    // Get country prefix for multi-country support
    const countryPrefix = country !== 'RO' ? `/${country.toLowerCase()}` : '';
    
    // Map vehicle types to their dedicated pages
    const routeMap: Record<VehicleType, string> = {
      car: `${countryPrefix}/cars`,
      truck: `${countryPrefix}/camioane`,
      motorcycle: `${countryPrefix}/motociclete`,
      agricultural: `${countryPrefix}/utilaje`,
      construction: `${countryPrefix}/utilaje`,
      boat: country === 'UK' ? `${countryPrefix}/boats` : `${countryPrefix}/ambarcatiuni`,
      van: `${countryPrefix}/vehicles?type=van`,
      trailer: `${countryPrefix}/vehicles?type=trailer`,
      atv: `${countryPrefix}/vehicles?type=atv`
    };
    
    const route = routeMap[type] || `${countryPrefix}/vehicles?type=${type}`;
    navigate(route);
  };

  const clearFilter = () => {
    setFilters({});
    window.history.pushState({}, '', '/vehicles');
  };

  const handleQuickView = async (vehicle: any) => {
    // Map v2 listing data to quick view format
    const mappedVehicle: any = {
      id: vehicle.id,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      price: vehicle.price,
      mileage: vehicle.mileage_value || 0,
      location: vehicle.location_city || vehicle.location_region || vehicle.location_country,
      images: vehicle.photos || [],
      description: vehicle.description_text,
      fuel_type: vehicle.attributes?.fuel,
      transmission: vehicle.attributes?.transmission,
      condition: vehicle.condition,
      vehicle_type: vehicle.category_code,
      promoted: vehicle.promoted,
      auto_promoted: vehicle.auto_promoted,
      is_new_listing: vehicle.is_new_listing,
    };
    setSelectedVehicle(mappedVehicle);
    setIsQuickViewOpen(true);

    // Contact details are never exposed in public listing payloads — fetch via secure RPC
    try {
      const { data } = await supabase.rpc('get_public_contact_info', { _listing_id: vehicle.id });
      const row = Array.isArray(data) ? (data[0] as any) : (data as any);
      if (row) {
        setSelectedVehicle({
          ...mappedVehicle,
          contact_phone: row.contact_phone || undefined,
          contact_email: row.contact_email || undefined,
        });
      }
    } catch {
      // silent — contact remains available on the detail page
    }
  };


  // Get vehicle type counts directly from database (fast, optimized query)
  const { data: categoryData } = useCategoryCounts(searchAllCountries ? null : country);
  const vehicleTypeCounts = categoryData?.counts || {
    car: 0,
    truck: 0,
    motorcycle: 0,
    van: 0,
    trailer: 0,
    agricultural: 0,
    construction: 0,
    boat: 0,
    atv: 0,
  };

  const getVehicleTypeLabel = (type?: VehicleType) => {
    if (!type) return t('all_vehicles');
    const labels: Record<VehicleType, string> = {
      car: t('cars_title'),
      truck: t('trucks_title'),
      motorcycle: t('motorcycles_title'),
      van: t('vans_title'),
      trailer: t('trailers_title'),
      agricultural: t('agricultural_title'),
      construction: t('construction_title'),
      boat: t('boats_title'),
      atv: t('atv_title')
    };
    return labels[type] || type;
  };

  const pageHeading = selectedVehicleType
    ? `${getVehicleTypeLabel(selectedVehicleType).replace(/\s+de vânzare$/i, '')} de vânzare`
    : 'Mașini de vânzare';

  return (
    <>
      <SEOHreflang canonicalUrl={canonicalUrl} />
      <FAQSchema faqs={[
        { 
          question: country === 'UK' ? 'What types of vehicles can I find?' : country === 'DE' ? 'Welche Fahrzeugtypen kann ich finden?' : 'Ce tipuri de vehicule pot găsi?',
          answer: country === 'UK' ? 'Browse available listings from dealers and private sellers.' : country === 'DE' ? 'Anzeigen von Händlern und Privatpersonen ansehen.' : 'Vezi anunțurile disponibile de la dealeri și persoane fizice.'
        },
        {
          question: country === 'UK' ? 'Are the vehicles verified?' : country === 'DE' ? 'Sind die Fahrzeuge geprüft?' : 'Sunt vehiculele verificate?',
          answer: country === 'UK' ? 'We verify dealer businesses, not every vehicle. Check the vehicle yourself before buying.' : country === 'DE' ? 'Wir prüfen die Händlerfirma, nicht jedes Fahrzeug. Prüfen Sie das Fahrzeug vor dem Kauf.' : 'Verificăm firma dealerului prin datele CUI, CAEN, RISCO și ANAF, nu starea fiecărui vehicul. Cere actele și verifică mașina înainte de cumpărare.'
        },
        {
          question: country === 'UK' ? 'How do I contact a seller?' : country === 'DE' ? 'Wie kontaktiere ich einen Verkäufer?' : 'Cum contactez un vânzător?',
          answer: country === 'UK' ? 'Use the contact options on the listing to reach the seller.' : country === 'DE' ? 'Nutzen Sie die Kontaktmöglichkeiten in der Anzeige.' : 'Folosește opțiunile de contact din anunț pentru a vorbi cu vânzătorul.'
        }
      ]} />
      <Helmet>
        <html lang={language} />
        <title>
          {selectedVehicleType 
            ? `${getVehicleTypeLabel(selectedVehicleType)} ${country === 'UK' ? 'UK' : country === 'DE' ? 'Deutschland' : 'Romania'} - RO Auto Market`
            : t('page_title')
          }
        </title>
        <meta name="description" content={
          totalCount > 0 
            ? t('meta_description_with_count').replace('{count}', totalCount.toLocaleString('ro-RO'))
            : t('meta_description')
        } />
        <meta name="robots" content={country === 'UK' || country === 'DE' ? "noindex, nofollow" : vehiclesListKind.kind === 'filtered' || (!loading && listings.length === 0) ? "noindex, follow" : "index, follow"} />
        {vehiclesListKind.kind !== 'filtered' && (
          <link rel="canonical" href={vehiclesListKind.kind === 'page' ? `${canonicalUrl}?page=${vehiclesListKind.page}` : canonicalUrl} />
        )}
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content={
          selectedVehicleType 
            ? `${getVehicleTypeLabel(selectedVehicleType)} ${country === 'UK' ? 'UK' : country === 'DE' ? 'Deutschland' : 'Romania'} - RO Auto Market`
            : t('page_title')
        } />
        <meta property="og:description" content={
          totalCount > 0 
            ? t('meta_description_with_count').replace('{count}', totalCount.toLocaleString('ro-RO'))
            : t('meta_description')
        } />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://roautomarket.ro/og-roautomarket.png" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@roautomarket" />
        <meta name="twitter:title" content={
          selectedVehicleType 
            ? `${getVehicleTypeLabel(selectedVehicleType)} ${country === 'UK' ? 'UK' : country === 'DE' ? 'Deutschland' : 'Romania'} - RO Auto Market`
            : t('page_title')
        } />
        <meta name="twitter:description" content={
          listings.length > 0 
            ? t('meta_description_with_count').replace('{count}', listings.length.toString())
            : t('meta_description')
        } />
        <meta name="twitter:image" content="https://roautomarket.ro/og-roautomarket.png" />
        
        {/* ItemList Schema for search engines */}
        {!loading && listings.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "numberOfItems": listings.length,
              "itemListElement": listings.slice(0, 20).map((vehicle: any, index: number) => {
                const item: any = {
                  "@type": "ListItem",
                  "position": index + 1,
                  "url": `https://roautomarket.ro${country !== 'RO' ? `/${country.toLowerCase()}` : ''}/listing/${vehicle.id}`,
                  "name": `${vehicle.make} ${vehicle.model} ${vehicle.year}`
                };
                // Only add image if it's a valid URL
                if (vehicle.photos?.[0] && vehicle.photos[0].startsWith('http')) {
                  item.image = vehicle.photos[0];
                }
                return item;
              })
            })}
          </script>
        )}
      </Helmet>

      <div className="container mx-auto max-w-7xl px-4 pt-1 pb-3 space-y-2.5 md:py-5 md:space-y-5">
        <div className="surface-dark band-bleed -mt-1 pt-2 pb-3 md:-mt-5 md:pt-5 md:pb-5">
        {/* SEO Breadcrumbs */}
        <Breadcrumbs items={[
          { 
            label: selectedVehicleType ? getVehicleTypeLabel(selectedVehicleType) : t('all_vehicles'), 
            href: '#' 
          }
        ]} />

        <header>
          <h1 className="text-[22px] leading-tight font-semibold not-italic text-foreground md:text-3xl">{pageHeading}</h1>
          <div className="mt-0.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <p className="text-sm text-muted-foreground">{totalCount.toLocaleString('ro-RO')} de anunțuri</p>
            <button
              type="button"
              onClick={() => setAiSearchOpen((open) => !open)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
            >
              <Sparkles className="h-4 w-4 shrink-0" />
              Caută descriind mașina
            </button>
          </div>
          {aiSearchOpen && <div className="mt-3 max-w-3xl"><NLSearchBar targetPath="/vehicles" compact /></div>}
        </header>
        </div>

        {/* Compact Filter Bar */}
        <VehiclesFilterBar
          selectedType={selectedVehicleType}
          make={filters.make || ''}
          model={filters.model || ''}
          priceMax={filters.priceTo ? String(filters.priceTo) : ''}
          yearMin={filters.yearFrom ? String(filters.yearFrom) : ''}
          onTypeChange={(type) => {
            if (type) handleVehicleTypeChange(type as VehicleType);
          }}
          onMakeChange={(m) => setFilters(prev => ({ ...prev, make: m, model: '' }))}
          onModelChange={(m) => setFilters(prev => ({ ...prev, model: m }))}
          onPriceMaxChange={(p) => setFilters(prev => ({ ...prev, priceTo: p ? Number(p) : undefined }))}
          onYearMinChange={(y) => setFilters(prev => ({ ...prev, yearFrom: y ? Number(y) : undefined }))}
          onSearch={() => {
            handleApplyFilters(filters);
          }}
          advanced={{
            mileageFrom: filters.mileageFrom != null ? String(filters.mileageFrom) : '',
            mileageTo: filters.mileageTo != null ? String(filters.mileageTo) : '',
            powerFrom: filters.powerFrom != null ? String(filters.powerFrom) : '',
            powerTo: filters.powerTo != null ? String(filters.powerTo) : '',
            euroNorm: filters.euro_norm || '',
            doors: filters.doors != null ? String(filters.doors) : '',
            bodyType: filters.bodyType || '',
            color: filters.color || '',
            driveType: filters.driveType || '',
            conditionTechnical: filters.conditionTechnical || '',
            sellerType: filters.sellerType || '',
            location: filters.location || '',
            features: filters.features || [],
          }}
          onAdvancedChange={(patch) => setFilters(prev => ({
            ...prev,
            ...(patch.mileageFrom !== undefined && { mileageFrom: patch.mileageFrom ? Number(patch.mileageFrom) : undefined }),
            ...(patch.mileageTo !== undefined && { mileageTo: patch.mileageTo ? Number(patch.mileageTo) : undefined }),
            ...(patch.powerFrom !== undefined && { powerFrom: patch.powerFrom ? Number(patch.powerFrom) : undefined }),
            ...(patch.powerTo !== undefined && { powerTo: patch.powerTo ? Number(patch.powerTo) : undefined }),
            ...(patch.euroNorm !== undefined && { euro_norm: patch.euroNorm || undefined }),
            ...(patch.doors !== undefined && { doors: patch.doors ? Number(patch.doors) : undefined }),
            ...(patch.bodyType !== undefined && { bodyType: patch.bodyType || undefined }),
            ...(patch.color !== undefined && { color: patch.color || undefined }),
            ...(patch.driveType !== undefined && { driveType: patch.driveType || undefined }),
            ...(patch.conditionTechnical !== undefined && { conditionTechnical: patch.conditionTechnical || undefined }),
            ...(patch.sellerType !== undefined && { sellerType: patch.sellerType || undefined }),
            ...(patch.location !== undefined && { location: patch.location || undefined }),
            ...(patch.features !== undefined && { features: patch.features }),
          }))}
        />


        {/* Active filter chips — Refero-style sticky on desktop */}
        <ActiveFilterChips
          filters={filters}
          selectedType={selectedVehicleType}
          vehicleTypeLabel={selectedVehicleType ? getVehicleTypeLabel(selectedVehicleType) : undefined}
          resultsCount={!loading ? listings.length : undefined}
          loading={loading}
          onRemove={(key) => {
            const k = String(key);
            if (k.startsWith('feat__')) {
              const feat = k.slice(6);
              const nextFeatures = (filters.features || []).filter((f) => f !== feat);
              const nextFilters = { ...filters, features: nextFeatures.length ? nextFeatures : undefined };
              setFilters(nextFilters);
              handleApplyFilters(nextFilters);
              return;
            }
            setFilters((prev) => {
              const next = { ...prev };
              delete next[key as keyof MobileFilters];
              if (key === 'make') delete next.model;
              return next;
            });
            handleApplyFilters({ ...filters, [key]: undefined, ...(key === 'make' ? { model: undefined } : {}) });
          }}
          onClearAll={() => {
            setFilters({});
            handleApplyFilters({});
          }}
        />

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <SaveSearchDialog
            currentFilters={{ ...(filters as Record<string, unknown>), ...readSavedSearchFields(filters as Record<string, unknown>), ...(selectedVehicleType ? { tip: selectedVehicleType } : {}) }}
          />
          <Select value={sort} onValueChange={handleSortChange}>
            <SelectTrigger className="h-11 w-full sm:w-64" aria-label={buyer.sort_label}><SelectValue>{buyer.sort_options[sort]}</SelectValue></SelectTrigger>
            <SelectContent>
              {Object.keys(VEHICLE_SORTS).map(value => <SelectItem key={value} value={value}>{buyer.sort_options[resolveVehicleSort(value)]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>


        {/* Category Showcase Grid (Cinch-style large image cards) */}
        <div><ListCityPicker /></div>
        <div className="hidden md:block">
          <CategoryShowcaseGrid
            selectedType={selectedVehicleType}
            onTypeChange={handleVehicleTypeChange}
            counts={vehicleTypeCounts}
          />
        </div>

        <div className="hidden md:block"><CompareInfoBanner /></div>

        {/* Search All Countries Filter */}
        <div className="hidden items-center justify-center gap-2 p-4 bg-card rounded border md:flex">
          <Checkbox
            id="search-all-countries"
            checked={searchAllCountries}
            onCheckedChange={(checked) => {
              setSearchAllCountries(!!checked);
              const params = new URLSearchParams(searchParams);
              if (checked) {
                params.set('search_all_countries', 'true');
              } else {
                params.delete('search_all_countries');
              }
              navigate(`?${params.toString()}`, { replace: true });
            }}
          />
          <label 
            htmlFor="search-all-countries" 
            className="text-sm font-medium cursor-pointer flex items-center gap-2"
          >
            <Globe className="h-4 w-4" />
            {tFilters('all_countries')}
          </label>
        </div>

        {/* Anchor for scrolling results */}
        <div ref={resultsRef} id="results" />

        {/* Intent badge — shown when user landed via a lifestyle pill */}
        {(() => {
          const intent = searchParams.get('intent');
          const preset = INTENT_PRESETS.find((p) => p.id === intent);
          if (!preset) return null;
          return (
            <div className="mb-3 flex items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm text-primary">
                <preset.icon className="h-3.5 w-3.5" />
                Rezultate pentru: <strong className="font-semibold">{preset.label}</strong>
                <button
                  type="button"
                  onClick={() => {
                    setFilters({});
                    window.history.pushState({}, '', '/vehicles');
                    window.location.reload();
                  }}
                  aria-label="Șterge filtrul"
                  className="ml-1 rounded-full p-0.5 hover:bg-primary/20"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            </div>
          );
        })()}


        {/* Results header: CSS-controlled responsive layout to avoid SSR hydration mismatch */}
        {!loading && (
          <>
            {/* Mobile: results count only — main filtering happens in VehiclesFilterBar above */}
            <div className="hidden">
              <p className="text-sm text-muted-foreground">
                {totalCount.toLocaleString('ro-RO')} {t('results_found')}
              </p>
            </div>

            {/* Desktop: results count and view toggle */}
            {displayedListings.length > 0 && (
              <div className="hidden md:flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {totalCount.toLocaleString('ro-RO')} {t('results_found')}
                </p>
                <ViewToggle value={viewMode} onChange={setViewMode} />
              </div>
            )}
          </>
        )}

        {/* Vehicle Listings with Infinite Scroll */}
        {loading ? (
          <CarGridSkeleton count={8} />
        ) : displayedListings.length > 0 ? (
          <>
            {/* Mobile: compact horizontal cards */}
            <div>
            {isMobile ? (
              <div className="space-y-2 listings-grid">
                {displayedListings.map((vehicle) => <CompactMobileListingCard key={vehicle.id} listing={vehicle} />)}
              </div>
            ) : (
              /* Desktop: Grid or List View */
              <ListingsGridV2 
                listings={displayedListings}
                isLoading={loading}
                viewMode={viewMode}
              />
            )}
            </div>

            {/* Pagination */}
            <ListingsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalCount={totalCount}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </>
        ) : (
          <div style={{ minHeight: '500px' }}>
            <EmptyStateListings />
          </div>
        )}
        
        {country === 'RO' && (
          <div className="mt-8">
            <Button asChild variant="link" className="h-auto whitespace-normal px-0 text-left">
              <Link to="/evaluare-auto">{valuationCopy.vehicles_link}</Link>
            </Button>
          </div>
        )}

        {/* SEO Content Section */}
        {!loading && listings.length > 0 && (
          <div className="mt-16 pt-8 border-t">
            <h2 className="text-2xl font-bold mb-4">{t('seo_title')}</h2>
            <div className="space-y-4 text-muted-foreground max-w-3xl">
              <p dangerouslySetInnerHTML={{ __html: t('seo_intro') }} />
              <p dangerouslySetInnerHTML={{ __html: t('seo_dealer') }} />
              <p dangerouslySetInnerHTML={{ __html: t('seo_brands') }} />
            </div>
          </div>
        )}
      </div>

      {/* Quick View Modal */}
      <QuickViewModal
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        vehicle={selectedVehicle}
      />
    </>
  );
};

export default Vehicles;
